import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect } from 'react-redux';
import Tableau from '../../../UI/tableau/Tableau';

import AjouterCouche from './AjouterCouche/AjouterCouche';
import Bouton from '../../../UI/Bouton/Bouton';
import PopupSmall from '../../../UI/PopupSmall/PopupSmall.js';

import AttenteRequete from '../../../../hoc/AttenteRequete/AttenteRequete';

import { checkMot } from '../../../../ressources/functionJS/checkMot';
import { confirmeOnClick } from '../../../../ressources/functionJS/confirmeOnClick';
import * as requestType from '../../../../ressources/glossaires/glossaire_requetes';
import * as type_btn from '../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as actions from '../../../../services/actions';

import { VscSave } from 'react-icons/vsc';
import { MdRefresh } from 'react-icons/md';
import { RiFileAddFill } from 'react-icons/ri';
import * as s from '../../../../ressources/StyleGlobal/StyleGlobal';
import styles from './FormulaireCouche.module.scss';

const COLUMN_CONFIG = [
  ['layer_id', 'layer_id', 150],
  ['layer_type', 'layer_type', 160],
  ['layer_name', 'layer_name', 180],
  ['projection_id', 'projection_id', 150],
  ['layer_path', 'layer_path', 220],
  ['bounds_image', 'bounds_image', 180],
];

const createColumn = (field, labelKey, width) => ({
  dataField: field,
  text: checkMot(labelKey),
  style: { width },
  sort: true,
  editable: false,
  align: 'center',
  headerAlign: 'center',
});
const FormulaireCouche = (props) => {
  const {
    view_id,
    views_layers_list,
    reqRecupererListeCouchesVue,
    reqSupprimerCoucheVue,
    reqModifierOrdreCouches,
  } = props;

  const [rows, setRows] = useState([]);
  const [positionList, setPositionList] = useState([]);

  useEffect(() => {
    if (view_id !== undefined) {
      reqRecupererListeCouchesVue(view_id);
    }
  }, [view_id, reqRecupererListeCouchesVue]);

  useEffect(() => {
    if (view_id === undefined) return;
    const layers = views_layers_list?.[view_id];
    if (!layers) return;

    const newRows = layers.map((layer) => ({ ...layer }));
    const newPositions = layers.map((layer) => ({
      layer_id: parseInt(layer.layer_id),
      layer_position: parseInt(layer.position),
    }));

    setRows(newRows);
    setPositionList(newPositions);
  }, [view_id, views_layers_list]);

  const handleDelete = useCallback(
    (layerId) => {
      confirmeOnClick(
        checkMot('voulez_vous_vraiment_supprimer_cette_couche'),
        () => reqSupprimerCoucheVue(layerId)
      );
    },
    [reqSupprimerCoucheVue]
  );

  const handleSavePositions = useCallback(() => {
    if (view_id !== undefined) {
      reqModifierOrdreCouches(view_id, positionList);
    }
  }, [view_id, positionList, reqModifierOrdreCouches]);

  const processRowUpdate = useCallback((newRow) => {
    setRows((prev) =>
      prev.map((row) => (row.layer_id === newRow.layer_id ? newRow : row))
    );
    setPositionList((prev) =>
      prev.map((pos) =>
        pos.layer_id === newRow.layer_id
          ? {
              ...pos,
              layer_position: parseInt(newRow.position),
            }
          : pos
      )
    );
    return newRow;
  }, []);

  const columns = useMemo(
    () => [
      {
        dataField: 'delete',
        text: checkMot('supprimer'),
        style: { width: 120 },
        sort: false,
        editable: false,
        align: 'center',
        headerAlign: 'center',
        formatter: (cell, row) => (
          <AttenteRequete
            request_type={requestType.supprimer_une_couche_de_vue}
          >
            <Bouton cliquer={() => handleDelete(row.layer_id)}>
              {checkMot('supprimer')}
            </Bouton>
          </AttenteRequete>
        ),
      },
      {
        dataField: 'position',
        text: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{checkMot('position')}</span>
            <AttenteRequete
              request_type={requestType.supprimer_une_couche_de_vue}
            >
              <Bouton
                ajoutClass={styles.especila}
                hoverText={checkMot('enregistrer_le_changement_de_position')}
                type={type_btn.succeSimple}
                cliquer={handleSavePositions}
                desactive={false}
              >
                <VscSave size={20} />
              </Bouton>
            </AttenteRequete>
          </div>
        ),
        style: { width: 150 },
        sort: true,
        editable: true,
        align: 'center',
        headerAlign: 'center',
      },
      ...COLUMN_CONFIG.map(([field, labelKey, width]) =>
        createColumn(field, labelKey, width)
      ),
    ],
    [handleDelete, handleSavePositions]
  );

  const selectionConfig = useMemo(
    () => ({
      mode: 'radio',
      clickToSelect: false,
      hideSelectAll: true,
      hideSelectColumn: true,
    }),
    []
  );

  return (
    <Fragment>
      <div className={s.row}>
        <div className={[s.col_12, styles.marge].join(' ')}>
          <PopupSmall
            typeBoutonAffichage={type_btn.outil}
            icone={<RiFileAddFill />}
            hoverText={checkMot('ajouter_une_couche')}
          >
            <AjouterCouche view_id={view_id} />
          </PopupSmall>
          <AttenteRequete
            request_type={requestType.recuperer_la_liste_des_couches_de_vue}
          >
            <Bouton
              hoverText={checkMot('refresh')}
              type={type_btn.outil}
              cliquer={() => reqRecupererListeCouchesVue(view_id)}
            >
              <MdRefresh />
            </Bouton>
          </AttenteRequete>
        </div>
      </div>

      {views_layers_list?.[view_id] ? (
        <Tableau
          donnees={rows}
          colonnes={columns}
          cleLigne="layer_id"
          selectionnerLignes={selectionConfig}
          pageSizeOptions={[25, 50, 100]}
          initialPageSize={25}
          dataGridProps={{
            editMode: 'cell',
            processRowUpdate,
          }}
        />
      ) : (
        <Bouton cliquer={() => reqRecupererListeCouchesVue(view_id)}>
          {checkMot('refresh')}
        </Bouton>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    //import liste couches d'une vue
    views_layers_list: state.vue.views_layers_list,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    reqRecupererListeCouchesVue: (idVue) =>
      dispatch(actions.reqRecupererListeCouchesVue(idVue)),
    reqSupprimerCoucheVue: (idCouche) =>
      dispatch(actions.reqSupprimerCoucheVue(idCouche)),
    reqModifierOrdreCouches: (view_id, layers_position_order_list) =>
      dispatch(
        actions.reqModifierOrdreCouches(view_id, layers_position_order_list)
      ),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(FormulaireCouche);
